import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContact, selectContact } from '@repo/redux/actions/contacts';
import { fetchUserStats } from '@repo/redux/actions/user';
import AvatarFallback from '../AvatarFallback';
import cn from 'classnames';
import { Avatar } from '@repo/components/avatars';
import sharedStyles from '../WelcomeGuide.module.scss';
import styles from './Community.module.scss';
import { ActionStarredIcon } from '@repo/icons';
import { apiRequest } from '@repo/utils/helpers/api';
import { selectUserStats } from '@repo/redux/reducers/user/stats';
import { shuffleArray } from '@repo/utils/helpers/array';

type ContactAvatar = { avatar_url: string };

export default function Community({ onClick, user }) {
  const dispatch = useDispatch();
  const indexedUser = useSelector(selectContact(user.contact_id));
  const userStats = useSelector(selectUserStats);
  const [contactAvatars, setContactAvatars] = useState<(ContactAvatar | null)[]>([
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ]);
  const elapsedTime = userStats.earliestMoment.elapsedTime();

  let userAvatar = (
    <>
      <div className={styles.avatarBlur}>
        <Avatar url={indexedUser.avatarURL} size="xxl" />
      </div>
      <Avatar url={indexedUser.avatarURL} size="xxl" />
    </>
  );

  if (!indexedUser.avatarURL) {
    userAvatar = (
      <div className={styles.avatarFallback}>
        <div className={styles.fallbackName}>{user.first_name || 'YOU'}</div>
      </div>
    );
  }

  let statsSummary = (
    <p>
      Your relationships live in many places—when you connect an account to Clay, we'll bring in and
      build profiles for all the people you've met through that channel.
    </p>
  );

  if (userStats.contacts > 1) {
    statsSummary = (
      <p>
        Your relationships live in many places—we've brought in and built profiles for{' '}
        <span className={styles.contactCount}>{userStats.contacts.toLocaleString('en-US')}</span>{' '}
        people you've met
        {!userStats.moments || elapsedTime.value == -1 ? (
          '.'
        ) : (
          <>
            {' '}
            over the past <span className={styles.elapsedTime}>{elapsedTime.value}</span>{' '}
            {elapsedTime.unit} across{' '}
            <span className={styles.momentCount}>{userStats.moments.toLocaleString('en-US')}</span>{' '}
            moments.
          </>
        )}
      </p>
    );
  }

  const setContactAvatarsFromAPI = (response: ContactAvatar[]) => {
    setContactAvatars(shuffleArray(contactAvatars.map((_, idx) => response[idx])));
  };

  useEffect(() => {
    if (user.contact_id) {
      dispatch(fetchContact({ id: user.contact_id }));
    }
    dispatch(fetchUserStats());
    apiRequest<ContactAvatar[]>('GET', '/users/onboarding-avatars').then(setContactAvatarsFromAPI);
  }, []);

  return (
    <div className={cn(sharedStyles.page, sharedStyles.numberedPage)} onClick={onClick}>
      <div className={cn(sharedStyles.pageContent, sharedStyles.left)}>
        <h2>Your Community</h2>
        {statsSummary}
        <p>
          To help you get organized, we’ve used your relationship history to star{' '}
          <ActionStarredIcon hoverOpacity={false} className={styles.starIcon} /> who you’ve spent
          the most time with. By adding and removing people from this group you help Clay understand
          which relationships you’d like to focus on.
        </p>
      </div>
      <div className={styles.avatars}>
        <div className={styles.userAvatar}>{userAvatar}</div>
        <div className={styles.satelliteAvatars}>
          {contactAvatars.map((contact, i) => {
            let avatar = <AvatarFallback className={styles.satelliteAvatarFallback} />;

            if (contact?.avatar_url) {
              avatar = <Avatar url={contact.avatar_url} size="md" />;
            }

            return (
              <div className={styles.satelliteAvatar} key={i}>
                <div className={styles.avatarBlur}>{avatar}</div>
                {avatar}
              </div>
            );
          })}
        </div>
      </div>
      <div className={sharedStyles.pageNumber}>01 — Community</div>
    </div>
  );
}
